import { Link } from "react-router-dom";
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.already_subscribed ? "You're already on the list." : "Welcome aboard. Check your inbox.");
      setEmail("");
    } catch (err) {
      toast.error("That email didn't look right. Try again?");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-forest text-sand mt-24 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.06] bg-grain pointer-events-none" />
      <div className="container-x py-20 relative grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="font-serif text-4xl leading-[1.05] max-w-md">
            The weekly digest for Americans who book too many hotels.
          </div>
          <p className="mt-5 text-sand/70 max-w-md leading-relaxed">
            One email, every Sunday. The best rates, the cleanest comparisons, and the properties we'd book ourselves — for real.
          </p>          <form onSubmit={subscribe} data-testid="footer-newsletter-form" className="mt-8 flex gap-2 max-w-md">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@inbox.com"
              required
              data-testid="footer-newsletter-email"
              className="flex-1 bg-sand/10 border border-sand/20 px-4 py-3 text-sm text-sand placeholder:text-sand/50 focus:outline-none focus:border-terracotta"
            />
            <button
              type="submit"
              disabled={submitting}
              data-testid="footer-newsletter-submit"
              className="px-5 py-3 bg-terracotta hover:bg-terracotta-light transition-colors text-sm uppercase tracking-wide disabled:opacity-50"
            >
              {submitting ? "…" : "Subscribe"}
            </button>
          </form>
        </div>

        <div className="md:col-span-2">
          <div className="overline text-sand/50 mb-4">Explore</div>
          <ul className="space-y-3 text-sm">
            <li><Link to="/operators" className="hover:text-terracotta-light" data-testid="footer-link-operators">Operators</Link></li>
            <li><Link to="/guides" className="hover:text-terracotta-light" data-testid="footer-link-guides">Guides</Link></li>
            <li><Link to="/methodology" className="hover:text-terracotta-light" data-testid="footer-link-methodology">Methodology</Link></li>
            <li><Link to="/about" className="hover:text-terracotta-light" data-testid="footer-link-about">About</Link></li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <div className="overline text-sand/50 mb-4">Top operators</div>
          <ul className="space-y-3 text-sm">
            <li>
              <Link to="/operators/choice" className="inline-flex items-center gap-2 text-sand hover:text-terracotta-light font-medium" data-testid="footer-link-choice">
                <span className="text-terracotta-light">★</span> Choice Hotels
              </Link>
            </li>
            <li><Link to="/operators/booking-com" className="text-sand/80 hover:text-terracotta-light">Booking.com</Link></li>
            <li><Link to="/operators/hotels-com" className="text-sand/80 hover:text-terracotta-light">Hotels.com</Link></li>
            <li><Link to="/operators/marriott" className="text-sand/80 hover:text-terracotta-light">Marriott Bonvoy</Link></li>
            <li><Link to="/operators/hilton" className="text-sand/80 hover:text-terracotta-light">Hilton Honors</Link></li>
          </ul>
        </div>
        <div className="md:col-span-3">
          <div className="overline text-sand/50 mb-4">Fine print</div>
          <p className="text-xs text-sand/60 leading-relaxed">
            Choose My Hotel is an independent editorial comparison service published by <span className="text-sand/85">OptimHub</span> (SIREN 534 057 088), Paris. Some outbound links are affiliate partnerships — they never change our reviews or rankings.
          </p>
          <Link to="/legal" data-testid="footer-finprint-legal" className="mt-4 inline-flex text-xs uppercase tracking-[0.16em] text-terracotta-light hover:text-sand link-underline">
            Read the legal notice
          </Link>
        </div>
      </div>
      <div className="border-t border-sand/10">
        <div className="container-x py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-sand/55">
          <div>© {new Date().getFullYear()} Choose My Hotel — Published by <span className="text-sand/85">OptimHub</span>, Paris.</div>
          <div className="flex flex-wrap gap-5">
            <Link to="/legal" className="hover:text-sand" data-testid="footer-link-legal">Legal notice</Link>
            <Link to="/legal#privacy" className="hover:text-sand" data-testid="footer-link-privacy">Privacy</Link>
            <Link to="/legal#terms" className="hover:text-sand" data-testid="footer-link-terms">Terms</Link>
            <Link to="/legal#affiliate" className="hover:text-sand" data-testid="footer-link-disclosure">Affiliate disclosure</Link>
            <Link to="/admin/login" className="hover:text-sand" data-testid="footer-admin-link">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
